"use client";

import { useEffect, useState } from "react";
import { Project } from "@/types";
import { getProjectFloorplanSummary } from "@/app/utils/buildings";
import SearchFilters, { ISearchFilterTerms } from "@/components/search/SearchFilters";
import ProjectCard from "@/components/ProjectCard";
import Link from "next/link";

const DEFAULT_LOCATION = { lat: 17.385044, lon: 78.486671 }; // Hyderabad center
const DEFAULT_RADIUS = 5000;
const PAGE_SIZE = 30;

function getSearchResultsTitle(searchTerms?: ISearchFilterTerms) {
  if (searchTerms) {
    const parts = [];
    if (searchTerms.minBedrooms) {
      parts.push(`${searchTerms.minBedrooms}+ bedrooms`);
    }
    if (searchTerms.homeType) {
      parts.push(searchTerms.homeType + "s");
    }
    if (searchTerms.lat && searchTerms.lon) {
      parts.push("near you");
    }
    return parts.length > 0
      ? `Showing results for ${parts.join(" ")}`
      : "Show all properties near you";
  }
  return `Showing all properties near you`;
}

const NearMeClient = () => {
  const [searchTerms, setSearchTerms] = useState<ISearchFilterTerms | undefined>();
  const [searchResults, setSearchResults] = useState<Project[] | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [hasMoreResults, setHasMoreResults] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [showMore, setShowMore] = useState<boolean>(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  const fetchSearchResults = async (terms: ISearchFilterTerms, page: number = 0) => {
    if (loading || loadingMore) return;
    if (!terms.lat || !terms.lon) return;
    if (page === 0) {
      setLoading(true);
      setSearchResults(undefined);
    } else {
      setLoadingMore(true);
    }
    try {
      const response = await fetch("/api/search", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ terms, page }),
      });
      if (!response.ok) {
        console.error("Failed to fetch search results:", response.statusText);
        return;
      }
      const data = await response.json();
      setShowMore(data.projects.length === PAGE_SIZE);
      if (page === 0) {
        setSearchResults(data.projects);
      } else {
        setSearchResults((prevResults) => [...(prevResults || []), ...data.projects]);
      }
      setSearchTerms(data.terms);
      setHasMoreResults(data.projects.length > 0);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error during search API call:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreResults = () => {
    if (searchTerms) fetchSearchResults(searchTerms, currentPage + 1);
  };

  useEffect(() => {
    // Request geolocation on mount
    if (!searchTerms || !searchTerms.lat || !searchTerms.lon) {
      let didSetLocation = false;
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            const terms: ISearchFilterTerms = {
              lat: latitude,
              lon: longitude,
              radius: DEFAULT_RADIUS,
            };
            setSearchTerms(terms);
            setLocationError(null); // Clear any previous warning
            fetchSearchResults(terms);
            didSetLocation = true;
          },
          (error) => {
            if (!didSetLocation) {
              setLocationError("Could not get your location. Showing results for Hyderabad.");
              const terms: ISearchFilterTerms = {
                lat: DEFAULT_LOCATION.lat,
                lon: DEFAULT_LOCATION.lon,
                radius: DEFAULT_RADIUS,
              };
              setSearchTerms(terms);
              fetchSearchResults(terms);
            }
          },
          { enableHighAccuracy: true, timeout: 5000 }
        );
      } else {
        setLocationError("Geolocation is not supported by your browser. Showing results for Hyderabad.");
        const terms: ISearchFilterTerms = {
          lat: DEFAULT_LOCATION.lat,
          lon: DEFAULT_LOCATION.lon,
          radius: DEFAULT_RADIUS,
        };
        setSearchTerms(terms);
        fetchSearchResults(terms);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (terms: ISearchFilterTerms) => {
    // Always preserve lat/lon/radius
    const merged = { ...searchTerms, ...terms };
    setSearchTerms(merged);
    fetchSearchResults(merged);
  };

  return (
    <div className="min-h-[75vh] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <SearchFilters
          onFilterChange={handleFilterChange}
          terms={searchTerms}
        />
        <div className="md:col-span-3">
          {locationError && (
            <div className="alert alert-warning mb-4">{locationError}</div>
          )}
          {loading && (
            <h1 className="text-2xl font-bold mb-4">Searching ...</h1>
          )}
          {!loading && (
            <>
              <h1 className="text-2xl font-bold mb-4">
                {getSearchResultsTitle(searchTerms)}
              </h1>
              <div className="mb-4">
                <Link href="/onboarding" className="btn btn-secondary">
                  Start a fresh search
                </Link>
              </div>
            </>
          )}
          {loading && (
            <div className="flex justify-center items-center h-64">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {!loading &&
              (searchResults && searchResults.length > 0 ? (
                searchResults.map((project, index) => {
                  const floorplanSummary = getProjectFloorplanSummary(project);
                  const { bedrooms, maxArea, minArea } = floorplanSummary;
                  return <ProjectCard key={project.uuid} project={project} />;
                })
              ) : (
                <div className="flex justify-center items-center col-span-2 h-64">
                  <p className="text-gray-500 text-lg">No results found.</p>
                </div>
              ))}
          </div>
          {showMore && (
            <div className="flex justify-center mt-8">
              <button
                className="btn btn-primary"
                onClick={loadMoreResults}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NearMeClient; 